"use client";

import whiteLogo from "@/Images/jobstacky_dark_logo.png";
import { ExpandLess, ExpandMore, Menu as MenuIcon, MenuOpen, Verified } from "@mui/icons-material";
import {
	alpha,
	AppBar,
	Box,
	Chip,
	Collapse,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Popover,
	Stack,
	Toolbar,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { type Navigation, type NavigationItem as NavItem, useSession } from "@toolpad/core";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AccountMenu } from "./AccountPopover";
import Notification from "./Notifications";
import { CustomSession } from "./Session";
import { SidebarFooter } from "./SidebarFooter";
import { UpgradeNowButton } from "./UpgradeNowButton";
import { useAuth } from "@/services/Firebase/Authcontext";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

interface DashboardLayoutProps {
	showBreadcrumbs?: boolean;
	navigation?: Navigation;
	children: React.ReactNode;
}

// Helper function to check if path matches pattern
const matchesPattern = (pathname: string, pattern?: string, segment?: string): boolean => {
	if (!pattern && !segment) return false;

	if (pattern) {
		// Convert pattern to regex
		const regexPattern = pattern
			.replace(/\{\/:[^}]+\}/g, "(/[^/]+)?") // Handle optional params like {/:uuid}
			.replace(/\*/g, ".*"); // Handle wildcards

		const regex = new RegExp(`^/${regexPattern}$`);
		return regex.test(pathname);
	}

	if (segment) {
		return pathname === `/${segment}` || pathname.startsWith(`/${segment}/`);
	}

	return false;
};

// Navigation item component
const NavigationItem = ({
	item,
	mini,
	pathname,
	level = 0,
	onItemClick,
}: {
	item: NavItem;
	mini: boolean;
	pathname: string;
	level?: number;
	onItemClick?: () => void;
}) => {
	const [expanded, setExpanded] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const theme = useTheme();
	const isSegmentItem = "segment" in item;
	const isActive = isSegmentItem ? matchesPattern(pathname, (item as any).pattern, item.segment) : false;
	const hasActiveChild = isSegmentItem
		? item.children?.some((child: any) => matchesPattern(pathname, child.pattern, child.segment))
		: false;

	useEffect(() => {
		if (isSegmentItem && hasActiveChild) {
			setExpanded(true);
		}
	}, [isSegmentItem, hasActiveChild]);

	// Handle different item types
	if (item.kind === "divider") {
		return (
			<Divider
				sx={{
					my: 1,
					mx: 2,
					borderColor: mini ? theme.palette.divider : alpha(theme.palette.text.primary, 0.2),
				}}
			/>
		);
	}

	if (item.kind === "header") {
		if (mini) return null;
		return (
			<ListSubheader
				sx={{
					backgroundColor: "transparent",
					fontWeight: "bold",
					fontSize: "0.75rem",
					color: theme.palette.text.secondary,
					textTransform: "uppercase",
					letterSpacing: 1,
					px: 2,
				}}
			>
				{item.title}
			</ListSubheader>
		);
	}

	// For page items
	if (isSegmentItem) {
		const hasChildren = item.children && item.children.length > 0;
		const paddingLeft = mini ? 1 : 2 + level * 2;

		const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
			if (mini && hasChildren) {
				// In mini mode, show popover for items with children
				setAnchorEl(event.currentTarget);
			} else if (!mini) {
				// In expanded mode, toggle expansion
				setExpanded(!expanded);
			}
		};

		const handlePopoverClose = () => {
			setAnchorEl(null);
		};

		const isPopoverOpen = Boolean(anchorEl);

		if (hasChildren) {
			return (
				<>
					<ListItem disablePadding>
						<ListItemButton
							onClick={handleItemClick}
							sx={{
								pl: paddingLeft,
								pr: 2,
								py: 1,
								borderRadius: 2,
								mx: 1,
								mb: 1,
								background:
									hasActiveChild || isActive
										? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 1)} 0%, ${alpha(
												theme.palette.primary.light,
												0.8
										  )} 100%)`
										: "transparent",
								"&:hover": {
									backgroundColor: alpha(theme.palette.primary.main, 0.05),
									transform: "translateX(4px)",
								},
								transition: "all 0.3s ease",
							}}
						>
							<ListItemIcon
								sx={{
									minWidth: mini ? 0 : 40,
									color: isActive
										? theme.palette.primary.contrastText
										: mini
										? theme.palette.text.secondary
										: theme.palette.text.primary,
									// Remove icon animation in mini mode
									filter: isActive && !mini ? "drop-shadow(0 0 6px rgba(0,0,0,0.18))" : "none",
									transition: "color 0.2s",
									py: isActive && mini ? 0.5 : 0,
								}}
							>
								{(item as any).icon}
							</ListItemIcon>
							{!mini && (
								<>
									<ListItemText
										primary={item.title}
										primaryTypographyProps={{
											fontSize: "0.9rem",
											fontWeight: hasActiveChild || isActive ? "bold" : "medium",
											sx: {
												color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
											},
										}}
									/>
									{expanded ? (
										<ExpandLess
											sx={{
												color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
											}}
										/>
									) : (
										<ExpandMore
											sx={{
												color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
											}}
										/>
									)}
								</>
							)}
						</ListItemButton>
					</ListItem>

					{/* Popover for mini mode */}
					{mini && (
						<Popover
							open={isPopoverOpen}
							anchorEl={anchorEl}
							onClose={handlePopoverClose}
							anchorOrigin={{
								vertical: "center",
								horizontal: "right",
							}}
							transformOrigin={{
								vertical: "center",
								horizontal: "left",
							}}
							sx={{
								"& .MuiPaper-root": {
									mt: 0.5,
									ml: 1,
									minWidth: 200,
									maxWidth: 300,
									background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(
										theme.palette.background.default,
										0.95
									)} 100%)`,
									backdropFilter: "blur(20px)",
									border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
									borderRadius: 2,
									boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
								},
							}}
						>
							<Box sx={{ py: 1 }}>
								<ListSubheader
									sx={{
										backgroundColor: "transparent",
										fontWeight: "bold",
										fontSize: "0.75rem",
										color: alpha(theme.palette.text.primary, 0.8),
										textTransform: "uppercase",
										letterSpacing: 1,
										px: 2,
										py: 1,
									}}
								>
									{item.title}
								</ListSubheader>
								<List component="div" disablePadding>
									{item.children?.map((child: any, index: number) => {
										const childIsActive = matchesPattern(pathname, child.pattern, child.segment);
										const childHref = child.segment ? `/${child.segment}` : "#";

										return (
											<ListItemButton
												key={`${child.segment || child.title}-${index}`}
												component={Link}
												href={childHref}
												onClick={() => {
													handlePopoverClose();
													onItemClick?.();
												}}
												sx={{
													pl: 2,
													pr: 2,
													py: 1,
													mx: 1,
													borderRadius: 1,
													background: childIsActive
														? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
																theme.palette.primary.light,
																0.05
														  )} 100%)`
														: "transparent",
													"&:hover": {
														backgroundColor: alpha(theme.palette.primary.main, 0.08),
														transform: "translateX(4px)",
													},
													transition: "all 0.2s ease",
												}}
											>
												<ListItemIcon
													sx={{
														minWidth: 32,
														color: childIsActive ? theme.palette.primary.main : theme.palette.text.secondary,
													}}
												>
													{child.icon}
												</ListItemIcon>
												<ListItemText
													primary={child.title}
													primaryTypographyProps={{
														fontSize: "0.85rem",
														fontWeight: childIsActive ? "bold" : "medium",
														color: childIsActive ? theme.palette.primary.main : theme.palette.text.primary,
													}}
												/>
											</ListItemButton>
										);
									})}
								</List>
							</Box>
						</Popover>
					)}

					{!mini && (
						<Collapse in={expanded} timeout="auto" unmountOnExit>
							<List component="div" disablePadding sx={{ pl: 2 }}>
								{item.children?.map((child: any, index: number) => (
									<NavigationItem
										key={`${child.segment || child.title}-${index}`}
										item={child}
										mini={mini}
										pathname={pathname}
										level={level + 1}
										onItemClick={onItemClick}
									/>
								))}
							</List>
						</Collapse>
					)}
				</>
			);
		}

		// Single navigation item
		const href = item.segment ? `/${item.segment}` : "#";

		return (
			<ListItem disablePadding>
				<motion.div
					initial={false}
					animate={isActive && !mini ? { scale: 1.04 } : { scale: 1 }}
					transition={{ type: "spring", stiffness: 300, damping: 20 }}
					style={{ width: "100%" }}
				>
					<ListItemButton
						component={Link}
						href={href}
						onClick={onItemClick}
						aria-label={item.title}
						title={mini ? item.title : undefined}
						sx={{
							pl: paddingLeft,
							pr: 2,
							py: 1,
							borderRadius: 2,
							mx: 1,
							mb: 1,
							position: "relative",
							overflow: "hidden",
							background: isActive
								? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 1)} 0%, ${alpha(
										theme.palette.primary.light,
										0.8
								  )} 100%)`
								: "transparent",
							color: isActive ? theme.palette.primary.contrastText : "inherit",
							boxShadow: isActive ? `0 2px 16px 0 ${alpha(theme.palette.primary.main, 0.25)}` : "none",
							"&:hover": {
								background: isActive
									? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 1)} 0%, ${alpha(
											theme.palette.primary.light,
											1
									  )} 100%)`
									: alpha(theme.palette.primary.main, 0.08),
								transform: "translateX(4px) scale(1.02)",
							},
							transition: "all 0.25s cubic-bezier(.4,2,.6,1)",
							fontWeight: isActive ? "bold" : "medium",
							zIndex: isActive ? 1 : "auto",
						}}
					>
						<ListItemIcon
							sx={{
								minWidth: mini ? 0 : 40,
								color: isActive
									? theme.palette.primary.contrastText
									: mini
									? theme.palette.text.secondary
									: theme.palette.text.primary,
								// Remove icon animation in mini mode
								filter: isActive && !mini ? "drop-shadow(0 0 6px rgba(0,0,0,0.18))" : "none",
								transition: "color 0.2s",
								py: isActive && mini ? 0.5 : 0,
							}}
						>
							{(item as any).icon}
						</ListItemIcon>
						{/* Visually hidden text for SEO/Screen Readers when mini */}
						{mini && (
							<span
								style={{
									position: "absolute",
									width: "1px",
									height: "1px",
									padding: "0",
									margin: "-1px",
									overflow: "hidden",
									clip: "rect(0, 0, 0, 0)",
									whiteSpace: "nowrap",
									borderWidth: "0",
								}}
							>
								{item.title}
							</span>
						)}
						{!mini && (
							<ListItemText
								primary={item.title}
								primaryTypographyProps={{
									fontSize: "0.95rem",
									fontWeight: isActive ? "bold" : "medium",
									letterSpacing: isActive ? 0.5 : 0,
									sx: {
										color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
										textShadow: isActive ? `0 2px 8px ${alpha(theme.palette.primary.dark, 0.25)}` : "none",
									},
								}}
							/>
						)}
					</ListItemButton>
				</motion.div>
			</ListItem>
		);
	}

	return null;
};

export function SidebarLayout({ children, showBreadcrumbs = true, navigation = [] }: DashboardLayoutProps) {
	const { subscriptionLoading } = useAuth();
	const theme = useTheme();
	const session = useSession<CustomSession>();
	const pathname = usePathname();
	// Tablets have enough horizontal space for the collapsed navigation rail.
	// Keep the temporary menu drawer for phone-sized screens only.
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

	const isDefaultPlan = session?.user?.subscription?.plan?.isDefault ?? true;
	const planName = session?.user?.subscription?.plan?.name ?? "Free";
	const isTrial = session?.user?.subscription?.isTrial ?? false;

	const [mobileOpen, setMobileOpen] = useState(false);
	const [collapsed, setCollapsed] = useState(true);

	// Track if the user is hovering the drawer (desktop only)
	const [isHoveringDrawer, setIsHoveringDrawer] = useState(false);

	// Throttling refs for mouse move optimization
	const throttleRef = useRef<NodeJS.Timeout | null>(null);
	const lastMouseXRef = useRef<number>(0);
	const THROTTLE_DELAY = 16; // ~60fps
	const TOLERANCE_ZONE = 8; // pixels of tolerance to prevent flickering

	// Auto-expand/collapse drawer on hover (desktop only)
	useEffect(() => {
		if (!isMobile && !isTablet) {
			if (isHoveringDrawer) {
				setCollapsed(false);
			} else {
				setCollapsed(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isHoveringDrawer, isMobile, isTablet]);

	// Handle mouse movement to detect sidebar hovering (optimized with throttling)
	const handleMouseMove = useCallback(
		(event: React.MouseEvent) => {
			// Early return for mobile/tablet
			if (isMobile || isTablet) return;

			const { clientX } = event;

			// Early return if mouse hasn't moved significantly
			if (Math.abs(clientX - lastMouseXRef.current) < 2) return;

			lastMouseXRef.current = clientX;

			// Clear existing throttle
			if (throttleRef.current) {
				clearTimeout(throttleRef.current);
			}

			// Throttle the expensive operations
			throttleRef.current = setTimeout(() => {
				const sidebarWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;
				const effectiveWidth = sidebarWidth + TOLERANCE_ZONE;

				// Check if mouse is over sidebar area (with tolerance)
				const isOverSidebar = clientX <= effectiveWidth;

				// Only update state if it actually changed
				if (isOverSidebar !== isHoveringDrawer) {
					setIsHoveringDrawer(isOverSidebar);
				}
			}, THROTTLE_DELAY);
		},
		[isMobile, isTablet, collapsed, isHoveringDrawer]
	);

	// Handle mouse leaving the viewport (optimized)
	const handleMouseLeave = useCallback(() => {
		// Clear any pending throttled calls
		if (throttleRef.current) {
			clearTimeout(throttleRef.current);
			throttleRef.current = null;
		}

		// Early return for mobile/tablet
		if (isMobile || isTablet) return;

		// Always collapse when mouse leaves the viewport
		if (isHoveringDrawer) {
			setIsHoveringDrawer(false);
		}
	}, [isMobile, isTablet, isHoveringDrawer]);

	// Cleanup throttle on unmount
	useEffect(() => {
		return () => {
			if (throttleRef.current) {
				clearTimeout(throttleRef.current);
			}
		};
	}, []);

	const handleDrawerToggle = () => {
		if (isMobile) {
			setMobileOpen(!mobileOpen);
		} else {
			setCollapsed(!collapsed);
		}
	};

	const handleMobileDrawerClose = () => {
		setMobileOpen(false);
	};

	const handleItemClick = () => {
		if (isMobile) {
			setMobileOpen(false);
		}
	};

	const drawerWidth = collapsed && !isMobile ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

	const DrawerContent = ({ mini = false }: { mini?: boolean }) => (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background:
					isMobile
						? theme.palette.background.paper
						: collapsed
						? theme.palette.background.paper
						: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
				backdropFilter: "blur(10px)",
				transition: theme.transitions.create("background", {
					easing: theme.transitions.easing.easeInOut,
					duration: theme.transitions.duration.enteringScreen,
				}),
				transitionDelay: collapsed ? theme.transitions.duration.leavingScreen : 0,
			}}
		>
			{/* Header */}
			<Box sx={{ minHeight: isMobile ? 60 : 68 }} />

			{/* Navigation */}
			<Box
				sx={{
					flexGrow: 1,
					overflowY: "auto",
					// Hide scrollbars for all browsers
					scrollbarWidth: "none", // Firefox
					"&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
					msOverflowStyle: "none", // IE/Edge
				}}
			>
				<List
					sx={{
						py: 1,
						px: 1,
					}}
				>
					{navigation.map((item, index) => {
						const key =
							item.kind === "divider"
								? `divider-${index}`
								: item.kind === "header"
								? `header-${item.title}-${index}`
								: "segment" in item
								? `${item.segment}-${index}`
								: `item-${index}`;

						return <NavigationItem key={key} item={item} mini={mini} pathname={pathname} onItemClick={handleItemClick} />;
					})}
				</List>
			</Box>

			{/* Footer */}
			<SidebarFooter mini={mini} />
		</Box>
	);

	return (
        <Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				overflow: "hidden",
				width: "100vw",
				height: "100dvh",
			}}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			onPointerLeave={handleMouseLeave}
		>
            {/* App Bar */}
            <AppBar
				position="fixed"
				elevation={0}
				sx={{
					background: "rgba(15, 23, 42, 0.7)",
					backdropFilter: "blur(40px) saturate(180%)",
					borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
					boxShadow: "0 4px 30px rgba(0, 0, 0, 0.2)",
					borderRadius: 0,
					zIndex: 1202,
					color: "white",
				}}
			>
				<Toolbar>
					<IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
						{(isMobile && mobileOpen) || (!isMobile && !collapsed) ? <MenuOpen /> : <MenuIcon />}
					</IconButton>

					<Link href="/dashboard" style={{ textDecoration: "none" }}>
						<Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
							<motion.div
								initial={{ opacity: 0, y: -20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								style={{ display: "flex" }}
							>
								<Image
									src={whiteLogo.src}
									alt="JobStacky"
									height={40}
									width={120}
									style={{ marginTop: "auto", marginBottom: "auto" }}
								/>
							</motion.div>
						</Box>
					</Link>

					<Box sx={{ flexGrow: 1 }} />

					{session?.user?.id && !subscriptionLoading && isDefaultPlan && <UpgradeNowButton />}

					{!subscriptionLoading && !isDefaultPlan && (
						<Link href="/account/subscription-management">
							<Tooltip title={`Current Plan: ${planName}${isTrial ? " (Trial)" : ""}`}>
							<Chip
								icon={<Verified sx={{ fontSize: "1rem" }} />}
								label={isTrial ? planName + " (Trial)" : planName}
								size="small"
								sx={{
									fontWeight: 700,
									fontSize: "0.75rem",
									cursor: "pointer",
									mr: 1,
									backgroundColor: "rgba(76, 175, 80, 0.15)",
									color: "white",
									border: "1px solid rgba(76, 175, 80, 0.3)",
									backdropFilter: "blur(10px)",
									boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
									transition: "all 0.3s ease",
									display: { xs: "none", sm: "inline-flex" },
									"&:hover": {
										backgroundColor: "rgba(76, 175, 80, 0.25)",
										transform: "translateY(-1px)",
										boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
									},
									"& .MuiChip-icon": {
										color: "white",
									},
								}}
							/>
						</Tooltip>
					</Link>				)}
					<Stack direction="row" spacing={1} alignItems="center">
						<Notification user={session?.user} />
						<AccountMenu />
					</Stack>
				</Toolbar>
			</AppBar>
            {/* Mobile Drawer */}
            <Drawer
				variant="temporary"
				open={mobileOpen}
				onClose={handleMobileDrawerClose}
				ModalProps={{
					keepMounted: true,
				}}
				sx={{
					display: { xs: "block", sm: "none" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: DRAWER_WIDTH,
						background: theme.palette.background.paper,
						backdropFilter: "blur(20px)",
						border: "none",
						boxShadow: `4px 0 24px ${alpha(theme.palette.common.black, 0.3)}`,
					},
				}}
			>
				<DrawerContent />
			</Drawer>
            {/* Desktop Drawer */}
            <Drawer
				variant="permanent"
				sx={{
					display: { xs: "none", sm: "block" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
						background: collapsed ? "transparent" : alpha(theme.palette.common.black, 0.2),
						backdropFilter: "blur(20px)",
						border: "none",
						transition: theme.transitions.create(["width", "background"], {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.enteringScreen,
						}),
						overflowX: "hidden",
					},
				}}
				open
			>
				<DrawerContent mini={collapsed} />
			</Drawer>
            {/* Main Content */}
            <Box
				sx={{
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				<Toolbar />
				<Box
					component="main"
					sx={{
						ml: { sm: `${DRAWER_WIDTH_COLLAPSED}px` },
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
						overflow: "auto",
						minHeight: 0,
					}}
				>
					{children}
				</Box>
			</Box>
        </Box>
    );
}
